
export default class SlidePlayer {
    currentSlide = 0;
    goalSlideIndex = 0;
    timer = 0;
    running = false;

    goalTime = 0;

    stopTheCurrentLoop = true;
    rafId: number | null = null;

    // keep track of the last time
    lastTime = 0;
    backwards = false;

    slideTimings: Array<number>;

    registeredRenderFunctions: Array<(timer: number) => void> = [];
    disposeMainAnimation!: () => void;

    onSlideUpdate: ((currentSlide: number) => void) | undefined;

    constructor(slideTimings: Array<number>) {
        this.slideTimings = slideTimings;

        this.setTimer = this.setTimer.bind(this);
        this.start = this.start.bind(this);
        this.pause = this.pause.bind(this);
        this.reset = this.reset.bind(this);
        this.loop = this.loop.bind(this);
        this.goToSlide = this.goToSlide.bind(this);
        this.previousSlide = this.previousSlide.bind(this);
        this.nextSlide = this.nextSlide.bind(this);
    }

    setOnSlideUpdate = (onSlideUpdate: (currentSlide: number) => void) => {
        this.onSlideUpdate = onSlideUpdate;
    };

    // TODO: think about a more functional slidePlayer
    // as this is not optimal
    setSlideTimings(slideTimings: Array<number>) {
        this.slideTimings = slideTimings;
    }

    addRenderFunction(animate: (timer: number) => void) {
        this.registeredRenderFunctions.push(animate);
        return () => this.removeRenderFunction(animate);
    }

    removeRenderFunction(animate: TODOAny) {
        this.registeredRenderFunctions = this.registeredRenderFunctions.filter((f) => f !== animate);
    }

    startLoop(animate: (timer: number) => void) {
        if (this.disposeMainAnimation) {
            this.disposeMainAnimation();
        }
        this.disposeMainAnimation = this.addRenderFunction(animate);
        if (this.stopTheCurrentLoop) {
            this.stopTheCurrentLoop = false;
            this.loop();
        }
    }

    stopLoop() {
        this.stopTheCurrentLoop = true;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        if (this.disposeMainAnimation) {
            this.disposeMainAnimation();
        }
    }

    get slideIndex() {
        return this.currentGoalSlide;
    }

    get isAtEnd() {
        return this.slideIndex >= this.slideTimings.length - 1;
    }

    getSlideFromTimer(timer: number): number {
        return this.slideTimings.findIndex((timing) => timing >= timer);
    }

    updateCurrentSlide() {
        const currentSlideIndex = this.getSlideFromTimer(this.timer);
        if (this.currentSlide !== currentSlideIndex) {
            this.currentSlide = currentSlideIndex;
            if (this.onSlideUpdate) {
                this.onSlideUpdate(this.currentSlide);
            }
        }
    }

    loop() {
        const now = +new Date();

        if (this.running) {
            const diff = now - this.lastTime;
            const diffToGoalTime = this.goalTime - this.timer;
            this.backwards = false;
            if (diffToGoalTime > 0) {
                this.timer += Math.min(diff, diffToGoalTime);
            } else if (diffToGoalTime < 0) {
                const delta = Math.min(diff, Math.abs(diffToGoalTime));
                if (delta !== 0) {
                    this.backwards = true;
                    console.log("backwards", delta);
                    this.timer -= delta;
                }
            } else {
                this.running = false;
            }
        }

        this.updateCurrentSlide();

        this.lastTime = now;
        if (this.stopTheCurrentLoop) {
            this.running = false;
            return;
        }

        for (const registeredRenderFunction of this.registeredRenderFunctions) {
            registeredRenderFunction(this.timer);
        }
        this.rafId = requestAnimationFrame(this.loop);
    }

    get currentGoalSlide() {
        return this.getSlideFromTimer(this.goalTime);
    }

    start() {
        this.lastTime = +new Date();
        this.running = true;
        this.goalTime = this.slideTimings[this.slideTimings.length - 1];
    }

    pause() {
        this.running = false;
    }

    toggle() {
        if (this.running) {
            this.pause();
        } else {
            this.start();
        }
    }

    reset() {
        this.pause();
        this.timer = 0;
        console.log("reset");
    }

    nextFrame() {
        this.pause();
        // we just assume 60fps for now
        this.timer = Math.min(this.timer + 1, this.slideTimings[this.slideTimings.length - 1]);
    }

    previousFrame() {
        this.pause();
        // we just assume 60fps for now
        this.timer = Math.max(0, this.timer - 1);
    }

    setTimer(timer: number) {
        this.running = false;
        this.timer = Math.round(timer);
        this.goalSlideIndex = this.getSlideFromTimer(this.timer);
        this.goalTime = this.timer;
    }

    previousSlide() {
        const { slideTimings } = this;
        const { currentSlide } = this;
        // do nothing
        if (currentSlide === 0) {
            return;
        }
        const goalSlideIndex = currentSlide - 1;
        const previousSlideTiming = slideTimings[goalSlideIndex];
        this.goalSlideIndex = goalSlideIndex;
        this.goalTime = previousSlideTiming;
        this.running = true;
    }

    nextSlide() {
        const { slideTimings } = this;
        const { currentSlide } = this;
        // do nothing
        if (currentSlide >= slideTimings.length - 1) {
            return;
        }
        const goalSlideIndex = currentSlide + 1;
        const nextSlideTiming = slideTimings[goalSlideIndex];
        this.goalSlideIndex = goalSlideIndex;
        this.goalTime = nextSlideTiming;
        this.running = true;
    }

    goToChapterAndSlide(_: TODOAny, slideIndex: number) {
        this.goToSlide(slideIndex);
    }

    goToSlide(slideIndex: number, smooth = false) {
        const { slideTimings } = this;
        const goalTime = slideTimings[slideIndex];
        this.currentSlide = slideIndex;
        if (this.onSlideUpdate) {
            this.onSlideUpdate(this.currentSlide);
        }
        if (smooth) {
            this.goalTime = goalTime;
            this.goalSlideIndex = slideIndex;
            this.running = true;
        } else {
            this.setTimer(goalTime);
        }
    }
}
